import React, { useContext, useEffect, useMemo } from 'react';
import {
  GrowthBook as GB,
  GrowthBookContext as GrowthBookSDKContext,
  GrowthBookProvider as GrowthBookSDKProvider,
} from '@growthbook/growthbook-react';
import { useSelector } from 'react-redux';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { getDealer } from '../../redux/dealers/selectors';

// Remote evaluation, the only mode: the browser POSTs attributes to /api/eval/<key>
// for resolved values rather than downloading the ruleset. Must be a remote-eval
// connection - the endpoints are exclusive, and a mismatch 400s into every flag off.
const growthBook = new GB({
  apiHost: process.env.REACT_APP_GB_API_HOST,
  clientKey: process.env.REACT_APP_GB_REMOTE_EVAL_CLIENT_KEY,
  remoteEval: true,
});

export type GrowthBook = {
  getFeatureValue: (key: Feature, fallback: any) => any;
  isFeatureEnabled: (feature: Feature) => boolean;
  setAttributes: (attributes: Record<string, any>) => Promise<void>;
};

export enum Feature {
  TEST = 'test',
  // Kill switch: hides the Fees tab in the dealer editor when Command Center owns fees.
  HIDE_DEALER_FEES_TAB = 'uar-3334-hide-chairman-fees-tab',
}

/** App-facing surface bound to one client; reads go straight through, so a
 * consumer re-render is all it takes to pick up new decisions. */
const adapt = (client: GB): GrowthBook => ({
  // Double gate: an off feature returns the fallback even when it carries a value.
  // Long-standing behavior, deliberately preserved.
  getFeatureValue: (key, fallback) =>
    client.isOn(key) ? client.getFeatureValue(key, fallback) : fallback,

  isFeatureEnabled: (feature) => client.isOn(feature),

  // Must be awaited by anyone reading a flag in the same block: under remote eval
  // the decisions come back from POST /api/eval, so a read on the next line is stale.
  setAttributes: async (attributes) =>
    client.setAttributes({ ...client.getAttributes(), ...attributes }),
});

export const GrowthBookProvider = ({
  children,
}: {
  children?: React.ReactNode;
}): React.ReactElement => {
  const dealer: Dealer = useSelector(getDealer);

  useEffect(() => {
    growthBook.init({ streaming: true });
  }, []);

  useEffect(() => {
    if (dealer) {
      // Not awaited on purpose: dealerId-dependent flags are read on a later render,
      // which the SDK's renderer callback triggers once the new decisions land.
      adapt(growthBook).setAttributes({
        dealerId: dealer.id,
      });
    }
  }, [dealer]);

  // The SDK's provider registers a renderer that the client invokes from setPayload -
  // on init, on a streaming update, and on a remote-eval re-evaluation. That is the
  // subscription this integration lacked: nothing re-rendered when features arrived.
  return React.createElement(
    GrowthBookSDKProvider,
    { growthbook: growthBook },
    children,
  );
};

export const useGrowthBook = (): GrowthBook => {
  // Reading the SDK context is what subscribes each consumer: the provider builds a
  // fresh context value whenever the renderer fires, so callers re-evaluate flags.
  // Absent a provider the context is `{}`, so fail loudly rather than on first read.
  const { growthbook } = useContext(GrowthBookSDKContext);

  if (!growthbook) {
    throw new Error('useGrowthBook must be used within a GrowthBookProvider');
  }

  return useMemo(() => adapt(growthbook), [growthbook]);
};
